import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useCompanyData } from '~/data/company';
import { Roles } from '~/data/user';
import { AbsenceCalendarLimitedReport } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarLimitedReport';
import { AbsenceCalendarReport } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport';
import { AbsencesReport } from '~/scenes/PortalModules/Analytics/People/AbsencesReport';
import { AnniversariesReport } from '~/scenes/PortalModules/Analytics/People/AnniversariesReport/component';
import { BirthdaysReport } from '~/scenes/PortalModules/Analytics/People/BirthdaysReport';
import { CompanyEquipmentReport } from '~/scenes/PortalModules/Analytics/People/CompanyEquipmentReport';
import { CustomDataReport } from '~/scenes/PortalModules/Analytics/People/CustomDataReport';
import { EmployeeEquipmentReport } from '~/scenes/PortalModules/Analytics/People/EmployeeEquipmentReport';
import { EmployeesCostCenterReport } from '~/scenes/PortalModules/Analytics/People/EmployeesCostCenter';
import { ExpiringDocumentsReport } from '~/scenes/PortalModules/Analytics/People/ExpiringDocumentsReport';
import { FormerEmployeesReport } from '~/scenes/PortalModules/Analytics/People/FormerEmployeesReport';
import { FTEReport } from '~/scenes/PortalModules/Analytics/People/FTEReport';
import { HeadcountReport } from '~/scenes/PortalModules/Analytics/People/HeadcountReport';
import { NewEmployeesReport } from '~/scenes/PortalModules/Analytics/People/NewEmployeesReport';
import { OverrideManagerReport } from '~/scenes/PortalModules/Analytics/People/OverrideManagerReport';
import { PersonnelContactsReport } from '~/scenes/PortalModules/Analytics/People/PersonnelContactsReport';
import { PersonnelListCustomReport } from '~/scenes/PortalModules/Analytics/People/PersonnelListCustomReport';
import { PersonnelListReport } from '~/scenes/PortalModules/Analytics/People/PersonnelListReport';
import { PersonnelReport } from '~/scenes/PortalModules/Analytics/People/PersonnelReport/component';
import { PositionChangesReport } from '~/scenes/PortalModules/Analytics/People/PositionChangesReport';
import { RolesListReport } from '~/scenes/PortalModules/Analytics/People/RolesListReport';
import { SubstituteManagerReport } from '~/scenes/PortalModules/Analytics/People/SubstituteManagerReport';
import { VacationBalancesReport } from '~/scenes/PortalModules/Analytics/People/VacationBalancesReport';
import { VacationCalendarReport } from '~/scenes/PortalModules/Analytics/People/VacationCalendarReport';
import { WorkExperienceReport } from '~/scenes/PortalModules/Analytics/People/WorkExperienceReport';
import {
  ClosedWorkflowEmploymentDetailsReport,
  OpenWorkflowEmploymentDetailsReport,
} from '~/scenes/PortalModules/Analytics/People/WorkflowReport/EmplyomentDetails';
import {
  ClosedWorkflowInternalPositionsReport,
  OpenWorkflowInternalPositionsReport,
} from '~/scenes/PortalModules/Analytics/People/WorkflowReport/InternalPositions';
import { ClosedWorkflowRewardingReport } from '~/scenes/PortalModules/Analytics/Rewarding/ClosedWorkflowsReport';
import { CompensationEqualityReport } from '~/scenes/PortalModules/Analytics/Rewarding/CompensationEqualityReport';
import { OpenWorkflowRewardingReport } from '~/scenes/PortalModules/Analytics/Rewarding/OpenWorkflowsReport';
import { WorkingHoursSummaryReport } from '~/scenes/PortalModules/Analytics/Rewarding/WorkingHoursSummaryReport';
import { WorktimeDimensionReport } from '~/scenes/PortalModules/Analytics/Rewarding/WorktimeDimensionReport';
import { WorktimeReport } from '~/scenes/PortalModules/Analytics/Rewarding/WorktimeReport/component';
import {
  NavigationRoutingStructure,
  useStructuredModuleRouting,
} from '~/scenes/PortalViews/components/StructuredModuleRouter';
import { hasOneOfRoles } from '~/services/Permissions';

export const useAnalyticsViews = () => {
  const { t } = useTranslation();

  const { companyData } = useCompanyData();

  const companyConfig = useMemo(() => {
    return {
      showAbsenceCalendarToAll: companyData.config.fComAbsCalendarShowAll,
    };
  }, [companyData]);

  const myRoles = useLoggedInUserRoles();

  const hasRoleAccess = useCallback(
    (roles: Roles[]) => {
      return hasOneOfRoles(myRoles, roles);
    },
    [myRoles],
  );

  /**
   * Old roles mapping
   * iHR = HR_STAFF, HR_ADMIN, HR_ADMIN_LIMITED, HR_DEV (Included but might be a mistake)
   * iManager = MANAGER, ALT_MANAGER
   * ROLE-HR_STAFF-MANAGER = iHR & iManager
   */

  const initialNavigationStructure: NavigationRoutingStructure[] =
    useMemo(() => {
      const { showAbsenceCalendarToAll } = companyConfig;

      const fullHRs = [Roles.HR_ADMIN, Roles.HR_ADMIN_LIMITED, Roles.HR_STAFF];
      const fullManagers = [Roles.MANAGER, Roles.ALT_MANAGER];
      const fullManagersAndHRs = [...fullHRs, ...fullManagers];

      const peopleHRs = [...fullHRs, Roles.HR_PEOP, Roles.HR_PEOP_DEV];
      const fullManagersAndPeopleHRs = [...fullManagers, ...peopleHRs];

      const rewardingHRs = [...fullHRs];

      const performanceHRs = [...fullHRs];

      const developmentHRs = [...fullHRs, Roles.HR_DEV, Roles.HR_PEOP_DEV];

      return [
        {
          path: 'company-dashboard',
          label: t('laCompDB'),
        },
        {
          path: 'team-dashboard',
          label: t('laTeamDB'),
          isAvailable: hasRoleAccess([
            ...fullManagersAndPeopleHRs,
            Roles.DEPUTY_MANAGER,
          ]),
          subPaths: [
            {
              path: 'team-report',
              label: t('laTeamRep'),
              isAvailable: hasRoleAccess(fullManagersAndPeopleHRs),
            },
            {
              path: 'absence-calendar',
              label: t('laAbsCal'),
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.DEPUTY_MANAGER,
              ]),
            },
          ],
        },
        {
          path: 'hr-dashboard',
          label: t('laHRDB'),
          isAvailable: hasRoleAccess([...peopleHRs, Roles.HR_DEV]),
        },
        {
          path: 'employee-dashboard',
          label: t('laEmpDB'),
        },
        {
          path: 'work-experience-dashboard',
          label: t('laWEDB'),
        },
        {
          path: 'people',
          label: t('laPeople'),
          subPaths: [
            {
              path: 'test',
              label: 'Test',
              component: <>Lorem ipsum</>,
              isAvailable: false,
            },
            {
              path: 'personnel-report',
              label: t('laPersonelReport'),
              component: <PersonnelReport />,
              isAvailable: hasRoleAccess([...fullManagersAndPeopleHRs]),
            },
            {
              path: 'headcount-report',
              label: t('laHeadCountReport'),
              component: <HeadcountReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'fte-report',
              label: t('laFTEReport'),
              component: <FTEReport />,
              isAvailable: hasRoleAccess([...fullManagersAndPeopleHRs]),
            },
            {
              path: 'personnel-list',
              label: t('laPersonelList'),
              component: <PersonnelListReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.IT_ADMIN,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'personnel-list-custom',
              label: t('laCustomPersonelList'),
              component: <PersonnelListCustomReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndHRs,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'personnel-contacts',
              label: t('laContactReport'),
              component: <PersonnelContactsReport />,
              isAvailable: hasRoleAccess([
                ...peopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'roles-list',
              label: t('laRoleList'),
              component: <RolesListReport />,
              isAvailable: hasRoleAccess([
                ...peopleHRs,
                Roles.MANAGER,
                Roles.IT_ADMIN,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'substitute-manager',
              label: t('laSubstituteManagerReport'),
              component: <SubstituteManagerReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
              ]),
            },
            {
              path: 'absences',
              label: t('laAbsencesReport'),
              component: <AbsencesReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.DEPUTY_MANAGER,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'absence-calendar',
              label: t('laAbsCal'),
              component: <AbsenceCalendarReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.DEPUTY_MANAGER,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'vacation-and-absence-calendar',
              label: t('laVacAbsCalendar'),
              component: <AbsenceCalendarLimitedReport />,
              isAvailable: showAbsenceCalendarToAll,
            },
            {
              path: 'vacations',
              label: t('laVacationCalendar'),
              component: <VacationCalendarReport />,
              isAvailable: !showAbsenceCalendarToAll,
            },
            {
              path: 'vacation-balances',
              label: t('laVacationBalancesReport'),
              component: <VacationBalancesReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'position-changes',
              label: t('laPosChanges'),
              component: <PositionChangesReport />,
              isAvailable: hasRoleAccess([...fullManagersAndPeopleHRs]),
            },
            {
              path: 'work-experience',
              label: t('laWorkExperienceReport'),
              component: <WorkExperienceReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'expiring-documents-report',
              label: t('laExpiringDocument'),
              component: <ExpiringDocumentsReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'birthdays',
              label: t('laBirthdays'),
              component: <BirthdaysReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'anniversaries',
              label: t('laWorkAnni'),
              component: <AnniversariesReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'company-equipment',
              label: t('laCompanyEquipment'),
              component: <CompanyEquipmentReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
                Roles.IT_ADMIN,
              ]),
            },
            {
              path: 'employee-equipment',
              label: t('laEmployeesEquipment'),
              component: <EmployeeEquipmentReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
                Roles.IT_ADMIN,
              ]),
            },
            {
              path: 'former-employees',
              label: t('laEndedEmpList'),
              component: <FormerEmployeesReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
                Roles.IT_ADMIN,
              ]),
            },
            {
              path: 'new-employees',
              label: t('laNewEmpList'),
              component: <NewEmployeesReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
                Roles.IT_ADMIN,
              ]),
            },
            {
              path: 'custom-data',
              label: t('laCustomData'),
              component: <CustomDataReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'override-manager-report',
              label: t('laOverrideMgrReport'),
              component: <OverrideManagerReport />,
              isAvailable: hasRoleAccess([
                ...peopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'employees-cost-center',
              label: t('laEmpCostCentersReport'),
              component: <EmployeesCostCenterReport />,
              isAvailable: hasRoleAccess([
                ...fullManagersAndPeopleHRs,
                Roles.HR_DEV,
                Roles.HR_SALARY,
                Roles.IT_ADMIN,
              ]),
            },
            {
              path: 'open-workflows',
              label: t('laOpenWorkflowsReport'),
              subPaths: [
                {
                  path: 'open-workflows-employment-details',
                  label: t('laOpenWorkflowsReportEmpDetails'),
                  component: <OpenWorkflowEmploymentDetailsReport />,
                  isAvailable: hasRoleAccess([
                    ...peopleHRs,
                    Roles.HR_DEV,
                    Roles.HR_SALARY,
                  ]),
                },
                {
                  path: 'open-workflows-internal-positions',
                  label: t('laOpenWorkflowsReportIntPosition'),
                  component: <OpenWorkflowInternalPositionsReport />,
                  isAvailable: hasRoleAccess([
                    ...peopleHRs,
                    Roles.HR_DEV,
                    Roles.HR_SALARY,
                  ]),
                },
              ],
            },
            {
              path: 'closed-workflows',
              label: t('laClosedWorkflowsReport'),
              subPaths: [
                {
                  path: 'closed-workflows-employment-details',
                  label: t('laOpenWorkflowsReportEmpDetails'),
                  component: <ClosedWorkflowEmploymentDetailsReport />,
                  isAvailable: hasRoleAccess([
                    ...peopleHRs,
                    Roles.HR_DEV,
                    Roles.HR_SALARY,
                  ]),
                },
                {
                  path: 'closed-workflows-internal-positions',
                  label: t('laOpenWorkflowsReportIntPosition'),
                  component: <ClosedWorkflowInternalPositionsReport />,
                  isAvailable: hasRoleAccess([
                    ...peopleHRs,
                    Roles.HR_DEV,
                    Roles.HR_SALARY,
                  ]),
                },
              ],
            },
          ],
        },
        {
          path: 'rewarding',
          label: t('laRewarding'),
          subPaths: [
            {
              path: 'compensation-equality',
              label: t('laCompensationEqualityReport'),
              component: <CompensationEqualityReport />,
              isAvailable: hasRoleAccess([...rewardingHRs, Roles.HR_SALARY]),
            },
            {
              path: 'open-workflows',
              label: t('laOpenWorkflowsReport'),
              component: <OpenWorkflowRewardingReport />,
              isAvailable: hasRoleAccess([
                ...rewardingHRs,
                Roles.HR_SALARY,
                Roles.MANAGER,
              ]),
            },
            {
              path: 'closed-workflows',
              label: t('laClosedWorkflowsReport'),
              component: <ClosedWorkflowRewardingReport />,
              isAvailable: hasRoleAccess([
                ...rewardingHRs,
                Roles.HR_SALARY,
                Roles.MANAGER,
              ]),
            },
            {
              path: 'worktime-report',
              label: t('laWorktimeReport'),
              component: <WorktimeReport />,
              isAvailable: hasRoleAccess([
                ...rewardingHRs,
                ...fullManagers,
                Roles.DEPUTY_MANAGER,
                Roles.IT_ADMIN,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'worktime-dimension-report',
              label: t('laWorktimeReportDimensions'),
              component: <WorktimeDimensionReport />,
              isAvailable: hasRoleAccess([
                ...rewardingHRs,
                ...fullManagers,
                Roles.DEPUTY_MANAGER,
                Roles.IT_ADMIN,
                Roles.HR_SALARY,
              ]),
            },
            {
              path: 'working-hours-summary',
              label: t('laWorkingHoursSummaryReport'),
              component: <WorkingHoursSummaryReport />,
              isAvailable: hasRoleAccess([
                ...rewardingHRs,
                ...fullManagers,
                Roles.DEPUTY_MANAGER,
                Roles.IT_ADMIN,
                Roles.HR_SALARY,
              ]),
            },
          ],
        },
        {
          path: 'performance',
          label: t('laPerformance'),
          isAvailable: hasRoleAccess([...performanceHRs]),
        },
        {
          path: 'development',
          label: t('laDevelopment'),
          isAvailable: hasRoleAccess([...developmentHRs, Roles.IT_ADMIN]),
        },
        // {
        //   path: 'organization',
        //   label: t('laOrganization'),
        // },
        // {
        //   path: 'succession',
        //   label: t('laSuccPlan'),
        // },
      ];
    }, [t, hasRoleAccess, companyConfig]);

  return useStructuredModuleRouting(initialNavigationStructure);
};
